import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Box, ArrowLeft, Eye } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products?category_id=${categoryId}`)
      ]);
      
      const foundCategory = categoriesRes.data.find(cat => cat.id === categoryId);
      setCategory(foundCategory);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xl">Loading products...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Breadcrumb & Header */}
        <section className="bg-muted/20 border-b">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4">
              <ArrowLeft className="h-4 w-4" />
              Back to Catalog
            </Link>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="category-heading">
              {category?.name || "Category"}
            </h1>
            {category?.description && (
              <p className="text-lg text-muted-foreground mt-2">{category.description}</p>
            )}
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {products.length === 0 ? (
              <div className="text-center py-16">
                <Box className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Products Available</h3>
                <p className="text-muted-foreground">Products in this category will appear here once added.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="product-card bg-white border border-border/50 rounded-sm overflow-hidden"
                    data-testid={`product-card-${product.code}`}
                  >
                    <div className="relative h-56 bg-gray-100">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Box className="h-16 w-16 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="mb-3">
                        <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                          {product.code}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold mb-3 line-clamp-2" style={{ fontFamily: 'Manrope' }}>
                        {product.name}
                      </h3>

                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Material:</span>
                          <span className="font-medium">{product.material}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Dimensions:</span>
                          <span className="font-medium text-right">{product.dimensions}</span>
                        </div>
                      </div>

                      {product.highlights && product.highlights.length > 0 && (
                        <ul className="space-y-1 mb-4 text-sm">
                          {product.highlights.slice(0, 2).map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-1">•</span>
                              <span className="text-muted-foreground">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <Link to={`/product/${product.id}`}>
                        <Button className="w-full gap-2" data-testid={`view-product-btn-${product.code}`}>
                          <Eye className="h-4 w-4" />
                          View Details & Pricing
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}